import React, { useEffect, useState, MouseEvent } from 'react';
import { Popconfirm, Popover } from 'antd';
import { StopOutlined, SyncOutlined } from '@ant-design/icons';
import { supportIncremental } from './cloudSourceReports/DestinationSelect';
import useStores from '@stores/useStores';
import { getApiErrorMessage } from '@components/common/util/util';
import Button from '@ui-library/button';
import { CatchErr } from '@utils/types';
import { useCloudSourceInfo } from '@stores/cloudSourcesInfo/cloudSourcesInfo';

type ButtonComponent = React.FC<{
  onClick: () => void;
  syncing: boolean;
  runFromDropDown: (e: { key: string; domEvent: MouseEvent<HTMLElement> }) => void;
}>;

const StartSyncButton = ({
  buttonComponent,
  isAdmin,
}: {
  buttonComponent?: ButtonComponent;
  isAdmin: boolean;
}) => {
  const ButtonComp = buttonComponent || Button;
  const { isPaused, stopping, isSyncing, starting, startStopSync, noConnections, source } =
    useCloudSourceInfo();
  const { workspaceStore, messagesStore, sourceDataStore } = useStores();
  if (noConnections || !isAdmin) {
    return null;
  }
  const showIncrementalButton = supportIncremental(
    source?.sourceDef?.name,
    source?.config?.syncBehaviour?.mode,
    workspaceStore.whActionsSyncBehaviours.length > 0,
  );
  const [isAvailableToSync, setAvailableToSync] = useState(false);

  const buttonText = (() => {
    if (isSyncing) {
      if (stopping) {
        return 'Stopping...';
      }
      if (starting) {
        return 'Starting...';
      }
      if (showIncrementalButton) {
        return 'Syncing';
      }
      return 'Stop';
    }
    return 'Sync Now';
  })();

  const buttonIcon = (() => {
    if (isSyncing) {
      if (showIncrementalButton) {
        return <SyncOutlined spin />;
      }
      return <StopOutlined />;
    }
    return <SyncOutlined />;
  })();

  const disabled = isPaused || stopping || starting;
  const nf = (e?: React.SyntheticEvent) => {
    const defaultSyncType = showIncrementalButton ? 'incremental' : 'full';
    startStopSync(e, defaultSyncType);
  };
  const runDifferentMode = (e: { key: string; domEvent: MouseEvent<HTMLElement> }) => {
    startStopSync(e.domEvent, e?.key);
  };

  const startButton = (
    <ButtonComp
      syncing={isSyncing}
      disabled={disabled || (showIncrementalButton && isSyncing) || !isAvailableToSync}
      loading={stopping || starting}
      onClick={!isSyncing ? nf : () => null}
      runFromDropDown={runDifferentMode}
      icon={buttonIcon}
    >
      <span>{buttonText}</span>
    </ButtonComp>
  );
  useEffect(() => {
    const checkIfAvailableToSync = async () => {
      try {
        await sourceDataStore.isSourceAvailableToSync(source.id);
        setAvailableToSync(true);
      } catch (err: CatchErr) {
        messagesStore.showErrorMessage(
          getApiErrorMessage(err, 'Syncs cannot be performed at this time.'),
        );
      }
    };
    if (source.enabled) {
      checkIfAvailableToSync();
    }
  }, []);

  if (!isPaused) {
    if (isSyncing && !disabled) {
      return (
        <div onClick={(e) => e.stopPropagation()}>
          <Popconfirm
            placement="left"
            title="Are you sure you want to stop syncing?"
            onConfirm={(e?: React.SyntheticEvent) => {
              startStopSync(e);
            }}
            okText="Ok"
          >
            {startButton}
          </Popconfirm>
        </div>
      );
    }
    if (!(disabled || (showIncrementalButton && isSyncing)) && !isAvailableToSync) {
      return (
        <Popover
          title="Setting up the configuration"
          content="Wait for some time, generally takes upto 30 sec."
          placement="left"
        >
          <span>{startButton}</span>
        </Popover>
      );
    }
    return startButton;
  }
  return (
    <Popover
      content="Your pipeline is paused. Make sure that source and at least one destination is enabled."
      title="Info"
      placement="left"
    >
      <span>{startButton}</span>
    </Popover>
  );
};

export default StartSyncButton;
